/**
 * Documents API Client
 * Handles all communication with the Document Service (port 10003)
 * Routes are project-scoped under /api/projects/:projectId/documents
 *
 * Features:
 * - Request timeout (30s default)
 * - Automatic retry with exponential backoff (3 retries)
 * - Proper error handling
 */

// API base URL - routed through Kong to document-service
const PROJECTS_API_BASE = '/api/projects';

// Default timeout in milliseconds
const DEFAULT_TIMEOUT = 30000;

// Maximum retry attempts
const MAX_RETRIES = 3;

// Base delay for exponential backoff (in ms)
const BASE_RETRY_DELAY = 1000;

class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

/**
 * Fetch with timeout using AbortController
 */
async function fetchWithTimeout(
  url: string,
  options: RequestInit,
  timeout: number = DEFAULT_TIMEOUT
): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });
    return response;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Sleep for a given number of milliseconds
 */
function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Fetch with retry logic and exponential backoff
 */
async function fetchWithRetry(
  url: string,
  options: RequestInit,
  retries: number = MAX_RETRIES,
  timeout: number = DEFAULT_TIMEOUT
): Promise<Response> {
  let lastError: Error | null = null;

  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      const response = await fetchWithTimeout(url, options, timeout);
      // Don't retry client errors (4xx) — they won't succeed on retry
      if (response.status >= 400 && response.status < 500) {
        return response;
      }
      return response;
    } catch (error) {
      lastError = error as Error;

      // Don't retry if aborted by user (not timeout)
      if (error instanceof DOMException && error.name === 'AbortError') {
        // Check if this was a timeout or user abort
        // Timeout errors we should retry, user aborts we shouldn't
        console.warn(`[DocumentsAPI] Request aborted (attempt ${attempt + 1}/${retries})`);
      }

      // Don't retry on non-retryable errors
      if (attempt < retries - 1) {
        const delay = BASE_RETRY_DELAY * Math.pow(2, attempt);
        console.warn(`[DocumentsAPI] Retrying in ${delay}ms (attempt ${attempt + 1}/${retries})`);
        await sleep(delay);
      }
    }
  }

  throw lastError || new Error('Request failed after retries');
}

async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = endpoint;

  if (process.env.NODE_ENV === 'development') {
    console.log('[DocumentsAPI]', options.method || 'GET', url);
  }

  const response = await fetchWithRetry(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    credentials: 'include',
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('[DocumentsAPI] Error:', response.status, errorText);
    throw new ApiError(response.status, errorText || response.statusText);
  }

  if (response.status === 204) {
    return null as T;
  }

  return response.json();
}

// Type definitions
export type DocumentType = 'document' | 'spec' | 'guide' | 'note' | 'template' | 'test';
export type DocumentStatus = 'draft' | 'in_progress' | 'review' | 'published' | 'archived';
export type ContentFormat = 'markdown' | 'html' | 'plain' | 'blocknote';

/**
 * Resolve a string to pass into the editor's initialContent prop.
 *
 * documents.pages.content is jsonb. For blocknote docs it comes back as an
 * actual array of blocks; for markdown/html/plain docs it's an empty array
 * and the real content lives in content_plain. The editor's parseContent
 * helper expects a string — a JSON string for blocknote, raw markdown for
 * the rest — so callers must not naively pass `doc.content`.
 */
export function resolveInitialContent(
  doc: {
    content?: unknown;
    content_plain?: string | null;
  } | null | undefined,
): string {
  if (!doc) return '';
  const raw = doc.content;
  const plain = doc.content_plain ?? '';
  if (Array.isArray(raw)) {
    return raw.length > 0 ? JSON.stringify(raw) : plain;
  }
  if (typeof raw === 'string' && raw.trim().length > 0) {
    return raw;
  }
  return plain;
}

export interface EditorDocument {
  id: string;
  project_id: string;
  worktree_id?: string | null;
  title: string;
  /**
   * jsonb payload from documents.pages.content. Historically a stringified JSON
   * blob, now an actual array of BlockNote blocks for blocknote-format docs,
   * and an empty array for markdown/html/plain docs (whose text lives in
   * content_plain). Callers should use resolveInitialContent() or similar to
   * derive a string to pass into the editor.
   */
  content: string | unknown[];
  /** Plain text / markdown projection — source of truth for markdown docs. */
  content_plain?: string | null;
  content_format: ContentFormat;
  doc_type: DocumentType;
  status: DocumentStatus;
  issue_id?: string;
  epic_id?: string;
  parent_doc_id?: string;
  tags: string[];
  metadata: Record<string, unknown>;
  summary?: string;
  current_version: number;
  created_at: string;
  updated_at: string;
  created_by?: string;
  updated_by?: string;
  deleted_at?: string | null;
  deleted_by?: string | null;
}

export interface EditorVersion {
  id: number;
  document_id: string;
  version_number: number;
  title: string;
  content: string;
  content_format: ContentFormat;
  change_summary?: string;
  change_type?: string;
  created_at: string;
  created_by?: string;
}

export interface DocumentListResponse {
  documents: EditorDocument[];
  total: number;
  page: number;
  page_size: number;
}

export interface DocumentWithContextResponse {
  document: EditorDocument;
  recent_chat: unknown[];
  active_contexts: unknown[];
  version_count: number;
}

export interface CreateDocumentRequest {
  title: string;
  content?: string;
  content_format?: ContentFormat;
  doc_type?: DocumentType;
  worktree_id?: string;
  issue_id?: string;
  epic_id?: string;
  parent_doc_id?: string;
  tags?: string[];
  metadata?: Record<string, unknown>;
  created_by?: string;
}

export interface UpdateDocumentRequest {
  title?: string;
  content?: string;
  content_format?: ContentFormat;
  status?: DocumentStatus;
  doc_type?: DocumentType;
  issue_id?: string;
  epic_id?: string;
  parent_doc_id?: string;
  tags?: string[];
  metadata?: Record<string, unknown>;
  updated_by?: string;
}

export interface DocumentSearchFilters {
  search?: string;
  doc_type?: DocumentType;
  status?: DocumentStatus;
  worktree_id?: string;
  issue_id?: string;
  epic_id?: string;
  tags?: string[];
  page?: number;
  page_size?: number;
}

// Link types
export type DocumentRelationType = 'parent' | 'child' | 'related' | 'blocks' | 'blocked_by' | 'implements' | 'tests' | 'linked';
export type LinkTargetType = 'document' | 'issue' | 'epic';

export interface DocumentLinkWithTarget {
  /**
   * Opaque link identifier — passed back to DELETE /:docId/links/:relationId.
   * Doc-to-doc links use page_links.id (varchar); entity links use
   * doc_references.id (uuid). Both are strings at the wire level.
   */
  id: string;
  relation_type: DocumentRelationType;
  display_order: number;
  created_at: string;
  created_by?: string;
  target_type: LinkTargetType;
  target_document?: {
    id: string;
    title: string;
    doc_type: DocumentType;
    status: DocumentStatus;
  };
  target_entity_id?: string;
}

export interface DocumentLinksResponse {
  links: DocumentLinkWithTarget[];
  count: number;
}

export interface CreateDocumentLinkRequest {
  target_document_id?: string;
  target_type?: LinkTargetType;
  target_entity_id?: string;
  relation_type: DocumentRelationType;
}

// Comment types
export interface DocumentComment {
  id: string;
  document_id: string;
  selection_start: number;
  selection_end: number;
  selected_text: string;
  content: string;
  author_id: string;
  author_name: string;
  author_type: 'user' | 'martha';
  parent_id?: string;
  status: 'open' | 'resolved';
  created_at: string;
  updated_at: string;
}

export interface CreateCommentRequest {
  selection_start: number;
  selection_end: number;
  selected_text: string;
  content: string;
  author_id: string;
  author_name: string;
  author_type?: 'user' | 'martha';
}

export interface CreateReplyRequest {
  content: string;
  author_id: string;
  author_name: string;
  author_type?: 'user' | 'martha';
}

/**
 * Documents API
 */
export const documentsApi = {
  /**
   * List documents by project
   * Route: GET /api/projects/:projectId/documents
   */
  list: async (
    projectId: string,
    filters: DocumentSearchFilters = {}
  ): Promise<DocumentListResponse> => {
    const params = new URLSearchParams();
    if (filters.search) params.set('search', filters.search);
    if (filters.doc_type) params.set('doc_type', filters.doc_type);
    if (filters.status) params.set('status', filters.status);
    if (filters.worktree_id) params.set('worktree_id', filters.worktree_id);
    if (filters.issue_id) params.set('issue_id', filters.issue_id);
    if (filters.epic_id) params.set('epic_id', filters.epic_id);
    if (filters.tags?.length) params.set('tags', filters.tags.join(','));
    if (filters.page) params.set('page', String(filters.page));
    if (filters.page_size) params.set('page_size', String(filters.page_size));

    const query = params.toString();
    return request<DocumentListResponse>(
      `${PROJECTS_API_BASE}/${projectId}/documents${query ? `?${query}` : ''}`
    );
  },

  /**
   * Search documents within a project
   * Route: GET /api/projects/:projectId/documents/search
   */
  search: async (
    projectId: string,
    searchQuery: string,
    limit?: number
  ): Promise<{ documents: EditorDocument[]; count: number }> => {
    const params = new URLSearchParams({ q: searchQuery });
    if (limit) params.set('limit', String(limit));
    return request(`${PROJECTS_API_BASE}/${projectId}/documents/search?${params}`);
  },

  /**
   * Create a document in a project
   * Route: POST /api/projects/:projectId/documents
   */
  create: async (
    projectId: string,
    data: CreateDocumentRequest
  ): Promise<EditorDocument> => {
    return request(`${PROJECTS_API_BASE}/${projectId}/documents`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Get a single document
   * Route: GET /api/projects/:projectId/documents/:docId
   * Falls back to GET /api/documents/:docId when projectId is empty (workspace-scoped content items)
   */
  get: async (
    documentId: string,
    projectId: string,
    withContext: boolean = false
  ): Promise<EditorDocument | DocumentWithContextResponse> => {
    const params = withContext ? '?with_context=true' : '';
    if (!projectId) {
      return request(`/api/documents/${documentId}${params}`);
    }
    return request(`${PROJECTS_API_BASE}/${projectId}/documents/${documentId}${params}`);
  },

  /**
   * Update a document
   * Route: PATCH /api/projects/:projectId/documents/:docId
   */
  update: async (
    documentId: string,
    projectId: string,
    data: UpdateDocumentRequest
  ): Promise<EditorDocument> => {
    const url = !projectId
      ? `/api/documents/${documentId}`
      : `${PROJECTS_API_BASE}/${projectId}/documents/${documentId}`;
    return request(url, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  /**
   * Delete a document
   * Route: DELETE /api/projects/:projectId/documents/:docId
   */
  delete: async (documentId: string, projectId: string): Promise<void> => {
    return request(`${PROJECTS_API_BASE}/${projectId}/documents/${documentId}`, {
      method: 'DELETE',
    });
  },

  /**
   * Trash operations
   */
  trash: {
    /**
     * List trashed documents in a project
     * Route: GET /api/projects/:projectId/documents/trash
     */
    list: async (
      projectId: string,
      page?: number,
      pageSize?: number
    ): Promise<DocumentListResponse> => {
      const params = new URLSearchParams();
      if (page) params.set('page', String(page));
      if (pageSize) params.set('page_size', String(pageSize));
      const query = params.toString();
      return request<DocumentListResponse>(
        `${PROJECTS_API_BASE}/${projectId}/documents/trash${query ? `?${query}` : ''}`
      );
    },

    /**
     * Restore a trashed document
     * Route: POST /api/projects/:projectId/documents/:docId/restore
     */
    restore: async (projectId: string, documentId: string): Promise<EditorDocument> => {
      return request(`${PROJECTS_API_BASE}/${projectId}/documents/${documentId}/restore`, {
        method: 'POST',
      });
    },

    /**
     * Permanently delete a trashed document
     * Route: DELETE /api/projects/:projectId/documents/:docId/permanent
     */
    permanentDelete: async (projectId: string, documentId: string): Promise<void> => {
      return request(`${PROJECTS_API_BASE}/${projectId}/documents/${documentId}/permanent`, {
        method: 'DELETE',
      });
    },
  },

  /**
   * Version operations
   */
  versions: {
    /**
     * List document versions
     * Route: GET /api/projects/:projectId/documents/:docId/versions
     */
    list: async (
      documentId: string,
      projectId: string,
      limit?: number
    ): Promise<{ versions: EditorVersion[]; count: number }> => {
      const params = limit ? `?limit=${limit}` : '';
      return request(`${PROJECTS_API_BASE}/${projectId}/documents/${documentId}/versions${params}`);
    },

    /**
     * Get a specific version
     * Route: GET /api/projects/:projectId/documents/:docId/versions/:versionNumber
     */
    get: async (
      documentId: string,
      projectId: string,
      versionNumber: number
    ): Promise<EditorVersion> => {
      return request(
        `${PROJECTS_API_BASE}/${projectId}/documents/${documentId}/versions/${versionNumber}`
      );
    },

    /**
     * Restore a version
     * Route: POST /api/projects/:projectId/documents/:docId/versions/:versionNumber/restore
     */
    restore: async (
      documentId: string,
      projectId: string,
      versionNumber: number,
      restoredBy?: string
    ): Promise<EditorDocument> => {
      return request(
        `${PROJECTS_API_BASE}/${projectId}/documents/${documentId}/versions/${versionNumber}/restore`,
        {
          method: 'POST',
          body: JSON.stringify({ restored_by: restoredBy }),
        }
      );
    },
  },

  /**
   * Comment operations
   */
  comments: {
    /**
     * List document comments
     * Route: GET /api/projects/:projectId/documents/:docId/comments
     */
    list: async (
      documentId: string,
      projectId: string,
      status?: 'open' | 'resolved' | 'all'
    ): Promise<DocumentComment[]> => {
      const params = status && status !== 'all' ? `?status=${status}` : '';
      return request(`${PROJECTS_API_BASE}/${projectId}/documents/${documentId}/comments${params}`);
    },

    /**
     * Create a comment
     * Route: POST /api/projects/:projectId/documents/:docId/comments
     */
    create: async (
      documentId: string,
      projectId: string,
      data: CreateCommentRequest
    ): Promise<DocumentComment> => {
      return request(`${PROJECTS_API_BASE}/${projectId}/documents/${documentId}/comments`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    /**
     * Reply to a comment
     * Route: POST /api/projects/:projectId/documents/:docId/comments/:commentId/reply
     */
    reply: async (
      documentId: string,
      projectId: string,
      commentId: string,
      data: CreateReplyRequest
    ): Promise<DocumentComment> => {
      return request(
        `${PROJECTS_API_BASE}/${projectId}/documents/${documentId}/comments/${commentId}/reply`,
        {
          method: 'POST',
          body: JSON.stringify(data),
        }
      );
    },

    /**
     * Update a comment (e.g., resolve)
     * Route: PATCH /api/projects/:projectId/documents/:docId/comments/:commentId
     */
    update: async (
      documentId: string,
      projectId: string,
      commentId: string,
      data: { status?: 'open' | 'resolved'; content?: string }
    ): Promise<DocumentComment> => {
      return request(
        `${PROJECTS_API_BASE}/${projectId}/documents/${documentId}/comments/${commentId}`,
        {
          method: 'PATCH',
          body: JSON.stringify(data),
        }
      );
    },

    /**
     * Delete a comment
     * Route: DELETE /api/projects/:projectId/documents/:docId/comments/:commentId
     */
    delete: async (
      documentId: string,
      projectId: string,
      commentId: string
    ): Promise<void> => {
      return request(
        `${PROJECTS_API_BASE}/${projectId}/documents/${documentId}/comments/${commentId}`,
        {
          method: 'DELETE',
        }
      );
    },
  },

  /**
   * Link operations
   */
  links: {
    /**
     * List document links
     * Route: GET /api/projects/:projectId/documents/:docId/links
     */
    list: async (
      documentId: string,
      projectId: string
    ): Promise<DocumentLinksResponse> => {
      return request(`${PROJECTS_API_BASE}/${projectId}/documents/${documentId}/links`);
    },

    /**
     * Create a link
     * Route: POST /api/projects/:projectId/documents/:docId/links
     */
    create: async (
      documentId: string,
      projectId: string,
      data: CreateDocumentLinkRequest
    ): Promise<unknown> => {
      return request(`${PROJECTS_API_BASE}/${projectId}/documents/${documentId}/links`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    /**
     * Delete a link
     * Route: DELETE /api/projects/:projectId/documents/:docId/links/:relationId
     */
    delete: async (
      documentId: string,
      projectId: string,
      relationId: string
    ): Promise<void> => {
      return request(
        `${PROJECTS_API_BASE}/${projectId}/documents/${documentId}/links/${encodeURIComponent(relationId)}`,
        {
          method: 'DELETE',
        }
      );
    },

    /**
     * Get documents linked to an entity (issue or epic)
     * Route: GET /api/projects/:projectId/documents/by-entity?entity_type=X&entity_id=Y
     */
    byEntity: async (
      projectId: string,
      entityType: 'issue' | 'epic',
      entityId: string
    ): Promise<{ documents: EditorDocument[]; count: number }> => {
      return request(
        `${PROJECTS_API_BASE}/${projectId}/documents/by-entity?entity_type=${entityType}&entity_id=${entityId}`
      );
    },
  },
};
